import React from 'react';
import cn from 'classnames';
import ContactAvatar from '@components/ContactAvatar';
import ClayUserBadge from '@components/Contact/Badges/ClayUser';
import { Relationship } from '@components/ContactCard';
import ContactBirthday from '@components/Contact/Badges/ContactBirthday';
import { TrashIcon } from '@components/Icons';
import styles from './ContactHeader.module.scss';
import { ContactReconnectCadenceBadge } from '../Badges/ContactReconnectCadence';
import Contact from '@models/Contact';

function AvatarHeader({ contact, sidebar = false, className = null, children }) {
  return (
    <div className={cn(styles.header, className)}>
      <div className={styles.container}>
        <ContactAvatar
          className={cn(styles.avatar, sidebar && styles.avatarSmall)}
          size={sidebar ? 'lg' : 'xl'}
          contact={contact}
          bgGlow
        />
        <div className={styles.content}>{children}</div>
      </div>
    </div>
  );
}

export default function ContactHeader({
  contact,
  sidebar = false,
  className = null,
}: {
  contact: Contact;
  sidebar?: boolean;
  className?: string;
}) {
  const showBadges = !contact.isMemorialized || contact.lifespan;

  let badges = (
    <>
      <Relationship relationship={contact.relationship} />
      <ClayUserBadge contact={contact} />
      <ContactBirthday contact={contact} />
      {!sidebar && <ContactReconnectCadenceBadge contact={contact} />}
    </>
  );

  if (contact.isMemorialized && contact.lifespan) {
    badges = <span className={styles.lifespanBadge}>{contact.lifespan}</span>;
  }

  return (
    <AvatarHeader contact={contact} sidebar={sidebar} className={className}>
      <div className={cn(styles.details, sidebar && styles.sidebarDetails)}>
        <div className={styles.identity}>
          <h4 className={styles.name}>{contact.displayName}</h4>
          {!sidebar && contact.pronouns && (
            <span className={styles.pronouns}>{contact.pronouns}</span>
          )}
        </div>
        {contact.primaryLocation?.formatted && (
          <span className={`text-small ${styles.location}`}>
            {contact.primaryLocation.formatted}
          </span>
        )}
      </div>
      {showBadges && <div className={styles.badges}>{badges}</div>}
    </AvatarHeader>
  );
}

export function ArchivedContactHeader({ contact, sidebar = false, className = null }) {
  const hasDeletedDate = !!contact?.deleted?.isValid;
  return (
    <AvatarHeader contact={contact} sidebar={sidebar} className={className}>
      <div className={cn(styles.details, sidebar && styles.sidebarDetails)}>
        <h4 className={styles.name}>{contact.displayName}</h4>
      </div>
      {hasDeletedDate && (
        <div className={cn('information', styles.archivedDate)}>
          <TrashIcon width={11} height={11} color="textSecondary" hover={false} />
          &nbsp;{contact.deleted.relativeTime}
        </div>
      )}
    </AvatarHeader>
  );
}
